import { Injectable } from '@angular/core';
import { filterNilValue, Query, persistState } from '@datorama/akita';
import { SessionStore, SessionState, DecodedToken } from './session.store';
import {
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    pluck,
    shareReplay,
    take
} from 'rxjs/operators';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { combineLatest, from, iif, of } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { AppConfigQuery } from '../app-config/app-config.query';
import { ImpersonateQuery } from '../../../pages/admin/impersonate/state/impersonate.query';

@Injectable({ providedIn: 'root' })
export class SessionQuery extends Query<SessionState> {
    isLoading$ = this.selectLoading();
    loadingState$ = this.select('loadingState');

    isAuthenticated$ = this.appConfig.select('useLatestKeycloakFlow').pipe(
        take(1),
        mergeMap((useLatestKC) =>
            iif(
                () => useLatestKC,
                from(this.kc.isLoggedIn()),
                this.select('access_token').pipe(
                    map((token) => !!token && this.isTokenStillValid(token)),
                    distinctUntilChanged()
                )
            )
        )
    );

    token$ = this.select('access_token');

    /** Observable for decoded token - emits when token is updated */
    decodedToken$ = this.select('decodedToken');

    get primaryOwnerId() {
        return this.getValue().decodedToken?.primaryOwnerId || null;
    }

    /** Observable for token expiration in milliseconds, emits when token is updated */
    validUntil$ = this.decodedToken$.pipe(
        filterNilValue(),
        map((token) => token?.exp),
        map((date) => date * 1000),
        distinctUntilChanged(),
        shareReplay(1)
    );

    get user() {
        return this.getValue().decodedToken;
    }

    /** Observable for user scopes - emits when user scopes change */
    userScopes$ = this.select((state) => state.decodedToken?.scope).pipe(
        map((scopeString) => scopeString.split(' ')),
        shareReplay(1)
    );

    /** get current user scopes */
    get userScopes() {
        return this.getValue().decodedToken?.scope.split(' ');
    }

    /** Observable for impersonated user scopes - emits when user scopes change */
    impersonatedUserScope$ = this.impersonateQuery.userScopes$;

    /** Observable for user roles of main user (eg. asset manager or admin) - emits when user roles change */
    userRoles$ = this.select((state) => state.decodedToken?.realm_access.roles).pipe(
        shareReplay(1)
    );

    /** get current user roles */
    get userRoles() {
        return this.getValue().decodedToken?.realm_access?.roles;
    }

    /** Observable for user roles of active user (eg. asset manager or impersonated user) - emits when user roles change */
    activeUserRoles$ = combineLatest([
        this.userRoles$,
        this.impersonateQuery.userRoles$
    ]).pipe(
        map(
            ([mainUserRoles, impersonatedUserRoles]) =>
                impersonatedUserRoles || mainUserRoles
        ),
        shareReplay(1)
    );

    /** isPrimaryUserAdmin$
     * @description Observable for admin user status - emits when primary user role changes, i.e. differs from isAdminUser$ which considers impersonated user roles to
     * @returns Observable<boolean>
     */
    isPrimaryUserAdmin$ = this.appConfig.select('useLatestKeycloakFlow').pipe(
        take(1),
        mergeMap((useLatestKC) =>
            iif(
                () => useLatestKC,
                of(this.kc.isUserInRole('ROLE_ADMINISTRATOR')),
                this.userRoles$.pipe(
                    map((roles) => roles?.some((role) => role === 'ROLE_ADMINISTRATOR')),
                    distinctUntilChanged(),
                    shareReplay(1)
                )
            )
        )
    );

    // TODO: check with Robert how this impacts impersonation and do additional modification
    /** isAdminUser$
     * @description Observable for admin user status - emits when user roles change
     * @returns Observable<boolean>
     */
    isAdminUser$ = this.appConfig.select('useLatestKeycloakFlow').pipe(
        take(1),
        mergeMap((useLatestKC) =>
            iif(
                () => useLatestKC,
                of(this.kc.isUserInRole('ROLE_ADMINISTRATOR')),
                this.activeUserRoles$.pipe(
                    map((roles) => roles?.some((role) => role === 'ROLE_ADMINISTRATOR')),
                    distinctUntilChanged(),
                    shareReplay(1)
                )
            )
        )
    );

    get isAdminUser() {
        return this.appConfig.appConfig.useLatestKeycloakFlow
            ? this.kc.isUserInRole('ROLE_ADMINISTRATOR')
            : this.getValue().decodedToken?.realm_access.roles.includes(
                  'ROLE_ADMINISTRATOR'
              );
    }

    get isAssetManager() {
        return this.appConfig.appConfig.useLatestKeycloakFlow
            ? this.kc.isUserInRole('ROLE_ACT_ON_BEHALF')
            : this.getValue().decodedToken?.realm_access.roles.includes(
                  'ROLE_ACT_ON_BEHALF'
              );
    }

    userName$ = this.select((state) => state.decodedToken?.user_name);
    userFullName$ = this.select((state) => state.decodedToken?.name);
    userEmail$ = this.select((state) => state.decodedToken?.email);
    firstName$ = this.select((state) => state.decodedToken?.given_name);
    // TODO: two factor status should be refactored and returned from backend
    twoFactorStatus$ = this.select('user').pipe(
        pluck('totpStatus', 'twoFactorRequired'),
        map(
            (status) =>
                status || {
                    primary: null,
                    secondary: null
                }
        )
    );

    /** Observable for user switch - emits when user id in active token changes */
    userSwitch$ = this.decodedToken$.pipe(
        map((decodedToken) => decodedToken?.userId), // get user id from decoded token
        filter((userId) => !!userId), // only emit user id:s
        pairwise(), // keep previous value for comparison
        map(([prev, current]) => prev !== current), // if new use differs from last user return true
        filter((userSwitch) => userSwitch) // only emit when user id has changed
    );

    persistState: any;
    constructor(
        protected store: SessionStore,
        private kc: KeycloakService,
        private appConfig: AppConfigQuery,
        private impersonateQuery: ImpersonateQuery
    ) {
        super(store);
        this.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1)
            )
            .subscribe(() => {
                this.persistState = persistState({
                    storage: sessionStorage,
                    key: this.sessionStorageKey,
                    include: ['legal-entities']
                });
            });
    }

    /** isAuthenticated - if access token exists, compare its expiration date/time with current date/time
     * @returns {boolean}
     */
    isAuthenticated() {
        if (!this.getValue().access_token) {
            return false;
        }
        return this.isTokenStillValid(this.getValue().access_token);
    }

    isTokenStillValid(token: any) {
        return jwtDecode<JwtPayload>(token)?.exp * 1000 > new Date().getTime();
    }

    getRefreshToken() {
        return this.getValue().refresh_token;
    }

    getToken() {
        return this.getValue().access_token;
    }

    /**
     * This function checks if there is an impersonated user's access token and returns it if it exists.
     * If not, it returns the current session's access token.
     *
     * @returns {string | undefined} The effective access token, prioritizing impersonated user's token if available.
     */
    get effectiveAccessToken() {
        const impersonatedToken = this.impersonateQuery.getValue().token?.access_token;
        return impersonatedToken || this.getValue().access_token;
    }

    get access_token() {
        return this.getValue().access_token;
    }

    hasScopeAccess(scope: string): boolean {
        if (this.impersonateQuery.userScopes?.length > 0) {
            return this.impersonateQuery.userScopes.includes(scope);
        } else {
            return this.userScopes.includes(scope);
        }
    }

    hasRoleAccess(role: string): boolean {
        if (this.impersonateQuery.userRoles?.length > 0) {
            return this.impersonateQuery.userRoles.includes(role);
        } else {
            return this.userRoles.includes(role);
        }
    }

    /** sessionStorageKey - returns the sessionStorage key for the current user
     * @returns {string} - base64 encoded user id
     */
    get sessionStorageKey(): string {
        return btoa(this.user?.userId + '');
    }
}
